import {z} from 'zod'

import {apiFetch} from '../api/fetch'
import {toApiDateString, toApiTimeString} from './job-schedule-format'
import {type ReconcileLocationInput, toLocationRequest} from './location-input'
import {
    type CreateBillableLineItemInput,
    createBillableLineItemRequestSchema,
    type JobAssetAssignmentInput,
    type JobAssetRequirementInput,
    type JobDetail,
    jobDetailSchema,
    type JobListFilters,
    type JobListItem,
    jobListItemSchema,
    type JobPatchInput,
    jobPatchRequestSchema,
    type JobWorkerAssignmentInput,
    type JobWorkerRequirementInput,
    type TimeEntry,
    type TimeEntryInput,
    timeEntryRequestSchema,
    timeEntrySchema,
    type WorkerAssignmentTimeEntries,
    workerAssignmentTimeEntriesSchema,
} from './schemas'

function dateFilterToApiParam(value: JobListFilters['startDateFrom']): string | undefined {
    if (value == null) {
        return undefined
    }
    if (typeof value === 'string') {
        return value
    }
    return toApiDateString(value)
}

function toJobListQueryParams(filters: JobListFilters = {}): URLSearchParams {
    const params = new URLSearchParams()

    if (filters.projectId) {
        params.set('project_id', filters.projectId)
    }

    if (filters.customerId) {
        params.set('customer_id', filters.customerId)
    }

    const name = filters.name?.trim()
    if (name) {
        params.set('name', name)
    }

    const startDateFrom = dateFilterToApiParam(filters.startDateFrom)
    if (startDateFrom) {
        params.set('start_date_from', startDateFrom)
    }

    const startDateTo = dateFilterToApiParam(filters.startDateTo)
    if (startDateTo) {
        params.set('start_date_to', startDateTo)
    }

    if (filters.sortBy) {
        params.set('sort_by', filters.sortBy)
    }

    if (filters.sortDir) {
        params.set('sort_dir', filters.sortDir)
    }

    if (filters.status?.length) {
        for (const status of filters.status) {
            params.append('status', status)
        }
    }

    if (filters.mine) {
        params.set('mine', 'true')
    }

    return params
}

function toApiLocations(locations: ReconcileLocationInput[]) {
    return locations.map(toLocationRequest)
}

function toApiWorkerRequirements(requirements: JobWorkerRequirementInput[]) {
    return requirements.map((requirement) => ({
        ...(requirement.id ? {id: requirement.id} : {}),
        worker_type_id: requirement.workerTypeId,
        quantity: requirement.quantity,
        ...(requirement.billingRate ? {billing_rate: requirement.billingRate} : {}),
        ...(requirement.billingType ? {billing_type: requirement.billingType} : {}),
    }))
}

function toApiAssetRequirements(requirements: JobAssetRequirementInput[]) {
    return requirements.map((requirement) => ({
        ...(requirement.id ? {id: requirement.id} : {}),
        rental_asset_type_id: requirement.rentalAssetTypeId,
        quantity: requirement.quantity,
        ...(requirement.billingRate ? {billing_rate: requirement.billingRate} : {}),
        ...(requirement.billingType ? {billing_type: requirement.billingType} : {}),
    }))
}

function toApiWorkerAssignments(assignments: JobWorkerAssignmentInput[]) {
    return assignments.map((assignment) => ({
        ...(assignment.id ? {id: assignment.id} : {}),
        worker_id: assignment.workerId,
        start_date: assignment.startDate,
        start_time: assignment.startTime,
        end_date: assignment.endDate,
        end_time: assignment.endTime,
    }))
}

function toApiAssetAssignments(assignments: JobAssetAssignmentInput[]) {
    return assignments.map((assignment) => ({
        ...(assignment.id ? {id: assignment.id} : {}),
        rental_asset_id: assignment.rentalAssetId ?? null,
        third_party_rental_asset_offering_id: assignment.thirdPartyRentalAssetOfferingId ?? null,
        ...(assignment.quantity ? {quantity: assignment.quantity} : {}),
        start_date: assignment.startDate,
        start_time: assignment.startTime,
        end_date: assignment.endDate,
        end_time: assignment.endTime,
        notes: assignment.notes ?? null,
    }))
}

export async function listJobs(filters: JobListFilters = {}): Promise<JobListItem[]> {
    const params = toJobListQueryParams(filters)
    const query = params.toString()
    const path = query ? `/jobs?${query}` : '/jobs'
    const raw = await apiFetch<unknown>(path)
    return z.array(jobListItemSchema).parse(raw)
}

export async function getJob(id: string): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}`)
    return jobDetailSchema.parse(raw)
}

export async function patchJob(id: string, input: JobPatchInput): Promise<JobDetail> {
    const body = jobPatchRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/jobs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return jobDetailSchema.parse(raw)
}

export async function reportJobDidNotHappen(id: string, note?: string): Promise<void> {
    const trimmed = note?.trim()
    await apiFetch<unknown>(`/jobs/${id}/did-not-happen`, {
        method: 'POST',
        body: JSON.stringify(trimmed ? {note: trimmed} : {}),
    })
}

export async function updateJobLocations(
    id: string,
    locations: ReconcileLocationInput[],
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}/locations`, {
        method: 'PUT',
        body: JSON.stringify(toApiLocations(locations)),
    })
    return jobDetailSchema.parse(raw)
}

export async function updateJobWorkerRequirements(
    id: string,
    requirements: JobWorkerRequirementInput[],
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}/worker-requirements`, {
        method: 'PUT',
        body: JSON.stringify(toApiWorkerRequirements(requirements)),
    })
    return jobDetailSchema.parse(raw)
}

export async function updateJobAssetRequirements(
    id: string,
    requirements: JobAssetRequirementInput[],
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}/asset-requirements`, {
        method: 'PUT',
        body: JSON.stringify(toApiAssetRequirements(requirements)),
    })
    return jobDetailSchema.parse(raw)
}

export async function updateJobWorkerAssignments(
    id: string,
    assignments: JobWorkerAssignmentInput[],
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}/worker-assignments`, {
        method: 'PUT',
        body: JSON.stringify(toApiWorkerAssignments(assignments)),
    })
    return jobDetailSchema.parse(raw)
}

export async function updateJobAssetAssignments(
    id: string,
    assignments: JobAssetAssignmentInput[],
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(`/jobs/${id}/asset-assignments`, {
        method: 'PUT',
        body: JSON.stringify(toApiAssetAssignments(assignments)),
    })
    return jobDetailSchema.parse(raw)
}

export async function setThirdPartyUnitNumber(
    jobId: string,
    rentalAssetId: string,
    unitNumber: string | null,
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(
        `/jobs/${jobId}/third-party-equipment/${rentalAssetId}/unit-number`,
        {method: 'PUT', body: JSON.stringify({unit_number: unitNumber})},
    )
    return jobDetailSchema.parse(raw)
}

export async function createBillableLineItem(
    jobId: string,
    input: CreateBillableLineItemInput,
): Promise<JobDetail> {
    const body = createBillableLineItemRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/jobs/${jobId}/billable-line-items`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return jobDetailSchema.parse(raw)
}

export async function updateBillableLineItem(
    jobId: string,
    lineItemId: string,
    input: CreateBillableLineItemInput,
): Promise<JobDetail> {
    const body = createBillableLineItemRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/jobs/${jobId}/billable-line-items/${lineItemId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return jobDetailSchema.parse(raw)
}

export async function archiveBillableLineItem(
    jobId: string,
    lineItemId: string,
): Promise<JobDetail> {
    const raw = await apiFetch<unknown>(
        `/jobs/${jobId}/billable-line-items/${lineItemId}/archive`,
        {method: 'POST'},
    )
    return jobDetailSchema.parse(raw)
}

export function jobScheduleToAssignmentFields(job: JobDetail) {
    return {
        startDate: job.startDate,
        startTime: job.startTime,
        endDate: job.endDate,
        endTime: job.endTime,
    }
}

export function jobScheduleInputToPatch(values: {
    startDate: Date
    startTime: string | null
    endDate: Date | null
    endTime: string | null
}): JobPatchInput {
    return {
        startDate: toApiDateString(values.startDate),
        startTime: toApiTimeString(values.startTime),
        endDate: values.endDate ? toApiDateString(values.endDate) : null,
        endTime: values.endDate ? toApiTimeString(values.endTime) : null,
    }
}

export async function listJobTimeEntries(jobId: string): Promise<WorkerAssignmentTimeEntries[]> {
    const raw = await apiFetch<unknown>(`/jobs/${jobId}/time-entries`)
    return z.array(workerAssignmentTimeEntriesSchema).parse(raw)
}

export async function createTimeEntry(
    jobId: string,
    assignmentId: string,
    input: TimeEntryInput,
): Promise<TimeEntry> {
    const body = timeEntryRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(
        `/jobs/${jobId}/worker-assignments/${assignmentId}/time-entries`,
        {
            method: 'POST',
            body: JSON.stringify(body),
        },
    )
    return timeEntrySchema.parse(raw)
}

export async function updateTimeEntry(
    jobId: string,
    entryId: string,
    input: TimeEntryInput,
): Promise<TimeEntry> {
    const body = timeEntryRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/jobs/${jobId}/time-entries/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    })
    return timeEntrySchema.parse(raw)
}

export async function deleteTimeEntry(jobId: string, entryId: string): Promise<void> {
    await apiFetch(`/jobs/${jobId}/time-entries/${entryId}`, {method: 'DELETE'})
}
